import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Search, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { href: '/specializations', label: 'Areas of Specialization' },
    { href: '/services', label: 'Services' },
    { href: '/resources', label: 'Resources' },
    { href: '/about', label: 'About Us' },
  ];

  return (
    <nav className="main-nav" data-testid="navbar">
      <div className="container-wide">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 py-2 no-underline" data-testid="logo-link">
            <img src="/air-logo.png" alt="Archon Insurance and Risk" className="h-20 object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${isActive(link.href) ? 'nav-link-active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="flex items-center gap-2 ml-4">
              <button className="p-2 text-[#333] hover:text-[var(--gold)]">
                <Search className="h-4 w-4" />
              </button>
              
              <Link to="/claims" className="nav-btn ml-2">
                Claims
              </Link>
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="nav-btn nav-btn-filled flex items-center gap-1">
                      My Account <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => window.location.href = '/dashboard'}>
                      Dashboard
                    </DropdownMenuItem>
                    {isAdmin() && (
                      <DropdownMenuItem onClick={() => window.location.href = '/admin'}>
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" className="nav-btn" data-testid="nav-client-login">
                    Client Login
                  </Link>
                  <Link to="/admin-login" className="nav-btn" data-testid="nav-admin-login">
                    Admin Login
                  </Link>
                  <Link to="/quote" className="nav-btn nav-btn-filled">
                    Contact Us
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block py-3 text-sm font-semibold uppercase tracking-wide text-[#333] no-underline border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/claims"
              className="block py-3 text-sm font-semibold uppercase tracking-wide text-[#333] no-underline border-b border-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Claims
            </Link>
            
            <div className="mt-4 space-y-2">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="btn-navy block text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Dashboard
                  </Link>
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      className="btn-outline-navy block text-center no-underline"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="btn-outline-navy w-full"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="btn-navy block text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Client Login
                  </Link>
                  <Link
                    to="/admin-login"
                    className="btn-outline-navy block text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Login
                  </Link>
                  <Link
                    to="/quote"
                    className="btn-outline-navy block text-center no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact Us
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
